import { computed, readonly, ref } from 'vue';
import { getDocumentHeight } from './scroll';

export function createWindowScrollTracker() {
  const position = ref(0);
  const groups = new Map<number, ReturnType<typeof createDirectionGroup>>();
  let subscribers = 0;
  let frame: number | null = null;

  function createDirectionGroup() {
    return { direction: ref<'up' | 'down' | null>(null), lockedUntil: 0, users: 0 };
  }

  function update() {
    frame = null;
    const current = window.scrollY;
    const diff = current - position.value;
    position.value = current;
    if (!diff) return;
    const direction = diff > 0 ? 'down' : 'up';
    const now = performance.now();
    for (const [delay, group] of groups) {
      if (group.users && group.direction.value !== direction && now >= group.lockedUntil) {
        group.direction.value = direction;
        group.lockedUntil = now + delay;
      }
    }
  }

  function onScroll() {
    // Несколько событий до следующего кадра обрабатываются одним обновлением.
    if (frame === null) frame = requestAnimationFrame(update);
  }

  const cookieHeight = computed(() => {
    // Одно измерение на позицию для всех потребителей.
    void position.value;
    if (!import.meta.client) return 0;
    return document.querySelector<HTMLElement>('.cookies-popup.show')?.clientHeight ?? 0;
  });

  const percentage = computed(() => {
    const current = position.value;
    if (!import.meta.client) return 0;
    const height = getDocumentHeight() - window.innerHeight;
    return height <= 0 ? 0 : Math.min(100, Math.round((current / height) * 100));
  });

  function consumer(delay: number) {
    let group = groups.get(delay);
    if (!group) {
      group = createDirectionGroup();
      groups.set(delay, group);
    }
    const state = group;
    let active = false;

    function start() {
      if (active) return;
      active = true;
      state.users++;
      if (subscribers++ === 0) {
        window.addEventListener('scroll', onScroll, { passive: true });
        update();
      } else if (state.users === 1 && position.value !== 0) {
        state.direction.value = 'down';
        state.lockedUntil = performance.now() + delay;
      }
    }

    function stop() {
      if (!active) return;
      active = false;
      if (--state.users === 0) {
        state.direction.value = null;
        state.lockedUntil = 0;
      }
      if (--subscribers === 0) {
        window.removeEventListener('scroll', onScroll);
        if (frame !== null) cancelAnimationFrame(frame);
        frame = null;
        position.value = 0;
      }
    }

    return { start, stop, direction: readonly(state.direction) };
  }

  return { position: readonly(position), cookieHeight, percentage, consumer };
}
